import React, { useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import { Icon, type IconName } from 'components/ui/icon';
import { Row } from 'components/ui/row';
import usePayment from 'state/payment/hooks/usePayment';
import { PAYMENTS } from 'state/payment/type';
import { formatToTz } from 'utils/date-format';
import { findEvent } from 'utils/timelineEvents';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import Notes from '../notepad/notes';
import { cn } from 'lib/utils';

interface IProps {
  info?: PAYMENTS.IIndividualTransactionResult;
  events?: PAYMENTS.IIndividualSession[];
  addNotes?: boolean;
  className?: string;
}

const Timeline = ({
  info = {} as PAYMENTS.IIndividualTransactionResult,
  events = {} as PAYMENTS.IIndividualSession[],
  addNotes = true,
  className = '',
}: IProps): JSX.Element => {
  const {
    id,
    order_id,
    updated_by_username,
    notes_added_date,
    timeline_notes,
  } = info;

  const [noteAreaOpen, setNoteAreaOpen] = useState(false);

  const toggle = () => setNoteAreaOpen((prevState) => !prevState);
  const [inputValue, setInputValue] = useState('');
  const { toAddNotes } = usePayment();
  const closeNoteArea = () => {
    setNoteAreaOpen(false);
    setInputValue('');
  };
  const { toFetchIndividualTransaction } = usePayment();
  const { payment } = usePayment();
  const request: PAYMENTS.IAddNotes = {
    notes_data: inputValue,
  };
  const {
    organisation: { settings },
  } = useOrganisation();

  const updateNote = () => {
    toAddNotes(id, request);
  };
  useEffect(() => {
    if (payment.addedNote) {
      toFetchIndividualTransaction(order_id);
      closeNoteArea();
    }
  }, [payment.addedNote]);

  const getEventDetails = (event: PAYMENTS.IIndividualSession) => {
    const { eventName, eventIcon } = findEvent.findEventName(event, info);
    // var for session log
    return { eventName, eventIcon };
  };

  // const uniqueEvents = events.length > 1 ? events?.filter((v, i, a) => a.findIndex(t => (t.name === v.name)) === i) : [];

  return (
    <>
      <div className={`mb-7 ${className}`}>
        <div className="flex justify-between items-center mb-2">
          <h3 className="mb-2 PaymentDetailsHeading">Timeline</h3>
          {addNotes && !noteAreaOpen && (
            <Button
              variant="secondary"
              onClick={toggle}
              size="md"
              className="flex items-center py-2 px-3 pl-2 bg-gray-blue-0 border border-gray-blue-20 shadow-sm"
            >
              <Icon name="plus" size={16} className="pr-1 pl-1" /> Add Note
            </Button>
          )}
        </div>
        {noteAreaOpen && (
          <Notes
            inputValue={inputValue}
            noteAreaOpen={noteAreaOpen}
            setInputValue={setInputValue}
            closeNoteArea={closeNoteArea}
            updateNote={updateNote}
          />
        )}
        <hr className="mt-0 mb-2 pb-1" />
        {timeline_notes && (
          <Row className="pl-4">
            <div className="pr-5 flex PL6">
              <div className="border-l border-gray-300 flex items-start">
                <Icon
                  name="note"
                  size={16}
                  className="-ml-[11px] p-1 text-gray-blue-40 bg-white mr-3"
                />
              </div>
              <div className="pb-4 DIB">
                <div className="TimelineSummaryHeading">{timeline_notes}</div>
                <div className="TimelineHeadingData">
                  <span>
                    {' '}
                    {formatToTz(
                      notes_added_date,
                      settings?.timezone,
                      'MMM dd, hh:mm a'
                    )}{' '}
                    by {updated_by_username}
                  </span>
                </div>
              </div>
            </div>
          </Row>
        )}

        {events.length >= 1 && (
          <>
            {events
              .slice()
              .reverse()
              .map((e, index) => {
                const sessionEventClass = getEventDetails(e);
                return (
                  <>
                    {e.create_date && sessionEventClass.eventName && (
                      <Row className="pl-4">
                        <div className="pr-5 flex PL6">
                          <div
                            className={cn(
                              'flex items-start',
                              index === events.length - 1
                                ? 'border-none ml-px'
                                : 'border-l border-gray-300'
                            )}
                          >
                            <Icon
                              name={sessionEventClass.eventIcon as IconName}
                              size={16}
                              className="-ml-[11px] p-1 text-gray-blue-40 bg-white mr-3"
                            />
                          </div>
                          <div className="pb-4 DIB">
                            <div className="TimelineSummaryHeading">
                              {sessionEventClass.eventName}
                            </div>
                            <div className="TimelineHeadingData">
                              {formatToTz(
                                e.create_date,
                                settings?.timezone,
                                'MMM dd, hh:mm a'
                              )}
                            </div>
                          </div>
                        </div>
                      </Row>
                    )}
                  </>
                );
              })}
          </>
        )}
      </div>
    </>
  );
};

export default Timeline;
